import { reactive, watch } from "vue";
import { defineStore } from "pinia";
import { api } from "@/helpers/api";
import { now } from "@/store/clock";
import { useUserStore } from "@/store/user";
import { createTicketCountOptions, getSubcategoryKey } from "@/helpers/subcategories";
import { useTicket } from "@/helpers/tickets";
import { ReservationsApi } from "@xs2event/js-lib/api";

let reservationTimeoutId = -1;
let cartMutationQueue = Promise.resolve();

const reservationsApi = new ReservationsApi();

import { getLocale } from "../components/page/menu/locales.js";

const getErrorStatus = (err: unknown): number | undefined => {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { status?: number } }).response;
    return response?.status;
  }
};

const INITIAL_STATE = () => ({
  allocationId: undefined,
  reservationId: undefined as string | undefined,
  itemCount: 0,
  upated: Date.now(),
  reservationTimeout: 0,
  updates: [],
  _items: {},
  items: [],
  loading: false,
  guestdata: [],
  reservationTotalTime: null, //10 minutes
  contactDetails: {
    first_name: null,
    last_name: null,
    street_name: null,
    number: null,
    postalcode: null,
    city: null,
    iso_country: null,
    phonenumber: null,
    email: null,
    marketing_optin: false,
    preferred_contact_language: getLocale(),
  },
  guestdataChanged: false,
  guestDataError: false,
  conditions: [],
  bookingEmail: undefined,
  paymentSelected: undefined,
  paymentReference: undefined,
  bookingReference: undefined,
  payDetails: null,
  hasVatErrors: false,
  distributor_vat: null,
  generalTermsAccepted: false,
  clientId: null,
  error: null,
  addGuestDataLater: false,
  bookingDetails: {}, // Store booking details from  checkout first step
});

export const useCartStore = defineStore("cart", {
  state() {
    return { ...INITIAL_STATE(), isOpen: false };
  },
  getters: {
    timeLeft() {
      const reservationTimeout = this.reservationTimeout;
      if (reservationTimeout) {
        const dt = reservationTimeout - now.value;
        if (0 < dt) {
          return dt;
        }
      }
    },
    getTotalTicketsInCart() {
      let numberOfTickets = 0;
      this.items.forEach((ticket) => {
        numberOfTickets = Number(numberOfTickets) + Number(ticket.amount);
      });
      return numberOfTickets;
    },
  },

  actions: {
    openCart() {
      this.isOpen = true;
      this.updates = [];
    },

    closeCart() {
      this.isOpen = false;
    },

    clearState() {
      cartMutationQueue = Promise.resolve();
      this.$patch(INITIAL_STATE());
    },

    async loadState(clientId) {
      cartMutationQueue = Promise.resolve();
      this.loading = false;
      this.clearState();
      if (!clientId) {
        return;
      }
      this.clientId = clientId;
      const json = localStorage[clientId];
      const hasData = "" < json;
      if (!hasData) {
        return;
      }
      try {
        const state = JSON.parse(json);
        this.allocationId = state.allocationId;

        //If the reservation has expired, and if reservation is allocation (on behalf), empty the cart
        if (this.allocationId && new Date() > state.reservationTimeout) {
          this.emptyCart();
          return;
        }

        if (state.reservationId) {
          const res = await api.get("/reservations/" + state.reservationId);
          this.updateTickets(res.data.items);
        }
        state.updates = [];
        this.$patch(state);
        this.initSubcategoryItems();
        if (state.reservationId) {
          this.updatePayDetails();
        }
      } catch (err) {
        console.error("loadState:", err.message);
        this.saveState();
      }
      this.updated = Date.now();
    },

    saveState() {
      try {
        const {
          clientId,
          bookingEmail,
          paymentReference,
          items,
          guestdata,
          conditions,
          allocationId,
          reservationId,
          reservationTimeout,
          reservationTotalTime,
        } = this;

        const non_cyclic_items = items.map((item) => ({
          ...item,
          ticket: {
            ...item.ticket,
            tickets: undefined, // if the ticket represented a subcategory it may contain (cyclic tickets)
          },
        }));

        if (clientId) {
          // console.info('saveState:', this.clientId)
          const json = JSON.stringify({
            bookingEmail,
            paymentReference,
            items: non_cyclic_items,
            guestdata,
            conditions,
            allocationId,
            reservationId,
            reservationTimeout,
            reservationTotalTime,
          });
          localStorage[clientId] = json;
        } else {
          // console.info('saveState:', 'no clientId')
        }
      } catch (err) {
        console.error("saveState:", err.message);
      }
    },

    totalNumberOfTickets() {
      let num = 0;
      this.items.forEach((e) => {
        num += e.amount;
      });
      return num;
    },

    getTicketAmount(ticket_id) {
      const n = this.items
        .filter((item) => item.ticket.ticket_id === ticket_id)
        .reduce((sum, item) => sum + item.amount, 0);
      return n;
    },

    getSubcategoryItem(event, ticket) {
      const items = this._items;
      const key = getSubcategoryKey(ticket);
      const item = (items[key] = items[key] || reactive({}));
      return item;
    },

    initSubcategoryItems() {
      this._items = {};
      this.items = this.items.map((item) => Object.assign(this.getSubcategoryItem(item.event, item.ticket), item));
      this.items.forEach((item) => {
        useTicket(item.ticket);
      });
    },

    async updatePayDetails() {
      try {
        if (this.reservationId) {
          const url = "/reservations/" + this.reservationId + "/pay-details";
          const req = api.get(url);
          const res = await req;
          return (this.payDetails = res.data);
        }
      } catch (err) {
        console.error(err.message);
      }

      return (this.payDetails = {
        total: {},
        ticket_fee: undefined,
        payment_fee: undefined,
        vat: {},
        tickets: [],
      });
    },

    async loadSubcategoryTickets(subcategory) {
      const params = {
        category_id: subcategory.category_id,
        event_id: subcategory.event_id,
        stock: "ge:1",
        ticket_status: "available",
        page_size: 99999,
        sorting: "category_name:asc,ticket_id:asc,face_value:asc",
      };
      const res = await api.get("/tickets?" + new URLSearchParams(params));
      subcategory.tickets = res.data.tickets
        .filter((ticket) => ticket.category_id !== "0")
        .filter(
          (ticket) => ticket.sub_category === subcategory.sub_category && ticket.category_id === subcategory.category_id
        )
        .map(useTicket);
    },

    async getSubcategoryOptions(subcategory) {
      if (!subcategory.tickets?.length) {
        await this.loadSubcategoryTickets(subcategory);
      }
      if (!subcategory.tickets?.length) {
        throw new ReferenceError("no tickets in subcategory");
      }
      return createTicketCountOptions(subcategory.tickets, 99999);
    },

    async findAmountInSubcategoryTickets(subcategory, amount) {
      const options = await this.getSubcategoryOptions(subcategory, 99999);

      const ticket = options.find((option) => option?.n === Number(amount))?.ticket;
      if (ticket) {
        return useTicket(ticket);
      }
    },

    async loadAllocationEvent(event_id) {
      const res = await api.get("/events/" + event_id);
      return res.data;
    },

    async loadAllocationTicket(ticket_id) {
      const res = await api.get("/tickets/" + ticket_id);
      return res.data;
    },

    async reserveAllocation(allocation) {
      this.allocationId = allocation.reservationId;
      const items = [];
      for (const item of allocation.items) {
        const ticket = await this.loadAllocationTicket(item.ticketId);
        const event = await this.loadAllocationEvent(ticket.event_id);

        items.push({
          ...item,
          // Ensure both snake_case and camelCase ticket IDs are present for compatibility
          // with the rest of cart logic that still reads ticket_id (snake_case).
          ticket_id: item.ticketId,
          allocation: item.quantity,
          amount: item.quantity,
          event,
          ticket: {
            ...ticket,
            net_rate: item.netRate,
            local_rates: { ...ticket.local_rates, net_rate_eur: item.netRate },
          },
        });
      }
      this.items = items;
      this.saveState();
      this.updateReservation();
    },

    async updateTickets(items) {
      if (this.allocationId) {
        this.items = this.items.filter((item) => {
          // Cart items from reserveAllocation use camelCase (ticketId) from ReservedItemResponse;
          // raw reservation response items use snake_case (ticket_id) — support both.
          const itemTicketId = item.ticket_id ?? item.ticketId;
          const updatedItem = items.find((x) => x.ticket_id == itemTicketId);
          return updatedItem;
        });

        if (!this.items.length) {
          this.allocationId = null;
        }

        this.saveState();

        console.log("updateTickets: skipped (allocationId)");
        return;
      }

      const index = {};
      this.items.forEach((item) => {
        const { ticket_id } = item.ticket;
        const ticket = useTicket({ ticket_id });
        index[ticket_id] = ticket;
        ticket.reserved = 0;
      });
      items.forEach((item) => {
        const { ticket_id } = item;
        const ticket = useTicket({ ticket_id });
        index[ticket_id] = ticket;
        ticket.reserved = item.quantity | 0;
      });

      for (const ticket_id in index) {
        api
          .get("/tickets/" + ticket_id)
          .then((res) => res.data)
          .then(useTicket);
      }
      this.items = this.items.filter((item) => item.amount);
    },

    async updateTicketAmount(event, subcategory, amount) {
      const run = async () => {
        this.loading = true;
        try {
          window.update = { event, subcategory, amount };

          const inCart = this.items.some((item) => getSubcategoryKey(item.ticket) === getSubcategoryKey(subcategory));
          if (inCart && !amount) {
            await this.remove(event, subcategory, amount);
          } else if (inCart && amount) {
            await this.update(event, subcategory, amount);
          } else if (!inCart && amount) {
            await this.add(event, subcategory, amount);
          }

          if (!this.items?.length) {
            this.deleteReservation().catch(console.assert);
          }
        } finally {
          this.loading = false;
        }
      };

      const queuedUpdate = cartMutationQueue.then(run, run);
      cartMutationQueue = queuedUpdate.catch(() => undefined);
      return queuedUpdate;
    },

    async add(event, subcategory, amount) {
      this.loading = true;
      const previousCartState = [...this.items];
      const item = this.getSubcategoryItem(event, subcategory);
      try {
        const ticket = await this.findAmountInSubcategoryTickets(subcategory, amount);
        Object.assign(item, {
          event: { ...event },
          ticket,
          amount,
          subCategory: getSubcategoryKey(ticket),
        });
        this.items = [...this.items, item];
        await this.updateReservation()
          .then(() => {
            this.showUpdate(item);
          })
          .catch((err) => {
            this.items = previousCartState;
            throw err;
          });
      } catch (err) {
        this.loading = false;
        throw err;
      }
    },

    async update(event, subcategory, amount) {
      const item = this.getSubcategoryItem(event, subcategory);

      if (item.amount === amount) {
        // Same amount selected again:
        // - reopen cart feedback
        // - restore reservation if it already expired
        if (!this.timeLeft) {
          this.loading = true;
          try {
            this.reservationId = null;
            await this.updateReservation("reserve-again");
          } catch (err) {
            this.loading = false;
            throw err;
          }
        }
        this.showUpdate(item);
        return;
      }
      this.loading = true;
      const restore = {
        ticket_id: item.ticket.ticket_id,
        stock: item.ticket.stock,
        reserved: item.ticket.reserved,
      };

      try {
        const ticket = await this.findAmountInSubcategoryTickets(subcategory, amount);
        // release current reservation because the update may be allocated on a different ticket_id
        useTicket({
          ticket_id: item.ticket.ticket_id,
          stock: item.ticket.stock + item.ticket.reserved,
          reserved: 0,
        });

        Object.assign(item, {
          ticket,
          amount,
        });
        await this.updateReservation();
        this.showUpdate(item);
      } catch (err) {
        // restore on any failed update path (reservation/ticket lookup)
        item.ticket = useTicket(restore);
        item.amount = restore.reserved;
        this.loading = false;
        throw err;
      }
    },

    async remove(event, subcategory) {
      this.loading = true;
      const item = this.getSubcategoryItem(event, subcategory);
      item.ticket.reserved = 0;
      item.amount = 0;
      try {
        await this.updateReservation();
        this.showUpdate(item);
      } catch (err) {
        this.loading = false;
        throw err;
      }
    },

    async getReservation() {
      const reservation = this.items;
      return await api.get("/reservations/" + this.reservationId, { items: reservation });
    },
    async updateReservation(action?) {
      this.loading = true;
      let shouldPreloadReservationDetails = false;
      try {
        const reservation = this.items
          .filter((x) => x.amount)
          .map((x) => ({
            ...x.ticket,
            tickets: undefined,
            quantity: x.amount,
          }));

        this.itemCount = reservation.reduce((sum, item) => sum + item.quantity, 0);

        const postdata = {
          parent_allocation_id: this.allocationId || undefined,
          items: reservation,
        };

        let res;
        if (this.reservationId) {
          try {
            res = await api.put("/reservations/" + this.reservationId, postdata); // strictly wait here
          } catch (err) {
            const status = getErrorStatus(err);
            if (status === 404) {
              // Server reservation no longer exists: recreate seamlessly.
              this.reservationId = null;
              this.reservationTimeout = null;
              res = await api.post("/reservations", postdata); // strictly wait here
            } else {
              throw err;
            }
          }
        } else {
          res = await api.post("/reservations", postdata); // strictly wait here
        }

        const reservationValidFrom = Date.parse(res.headers["x-serverutc"]);
        const reservationValidUntil = Date.parse(res.data.valid_until);
        const reservationTimeLeft = reservationValidUntil - reservationValidFrom;

        this.reservationTimeout = Date.now() + reservationTimeLeft;
        this.reservationTotalTime = reservationTimeLeft;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        clearTimeout(reservationTimeoutId as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reservationTimeoutId = setTimeout(() => this.dropReservation(), reservationTimeLeft) as any;

        this.reservationId = res.data.reservation_id;
        this.updateTickets(res.data.items);
        this.saveState();
        shouldPreloadReservationDetails = true;
      } catch (err) {
        this.error = err;
        throw err;
      } finally {
        this.loading = false;
      }

      const loadReservationDetails =
        action === "reserve-again"
          ? this.updatePayDetails().catch(console.assert)
          : Promise.all([this.updatePayDetails().catch(console.assert), this.fetchGuestData().catch(console.assert)]);

      // Keep the flyover responsive by preloading reservation details in the background.
      if (shouldPreloadReservationDetails) {
        loadReservationDetails.finally(() => {
          this.saveState();
        });
      }
    },

    async fetchGuestData() {
      if (this.reservationId) {
        const url = "/reservations/" + this.reservationId + "/guestdata?include_conditions=true";
        const req = api.get(url);
        const res = await req;
        this.setGuestData(res.data.items);
        this.guestdataChanged = false;
      }
    },

    getItem(ticket_id) {
      try {
        return this.items.find((item) => item.ticket.ticket_id === ticket_id);
      } catch {
        // ignore
      }
    },

    setGuestData(items) {
      if (items) {
        const list = this.items.map((item) => item.ticket.ticket_id);
        items.sort((a, b) => list.indexOf(a.ticket_id) - list.indexOf(b.ticket_id));
        this.conditions = items.slice();
        this.guestdata = items.map((item) => {
          return {
            ...item,
            guests: item.guests.map((guest) => {
              const values = {};
              for (const k in guest) {
                values[k] = guest[k].value || "";
              }
              values.guest_id = guest.guest_id || undefined;
              values.lead_guest = guest.lead_guest;
              return values;
            }),
          };
        });
      }
    },

    async updateGuestData(i, j, field, value) {
      this.guestdataChanged = true;
      this.guestdata[i].guests[j][field] = value;
    },

    async storeGuestData() {
      if (!this.addGuestDataLater) {
        try {
          if (this.reservationId) {
            await api.post("/reservations/" + this.reservationId + "/guestdata", {
              items: this.guestdata,
            });
          }
        } catch (err) {
          throw err.response;
        }
        await this.fetchGuestData();
        this.saveState();
      }
    },

    async storeContactDetails() {
      try {
        await api.post("/reservations/" + this.reservationId + "/contact-details", this.contactDetails);
      } catch (err) {
        const res = err.response;
        this.setGuestData(res.data.items);
        throw err;
      }
    },

    async deleteReservation() {
      const reservationId = this.reservationId;
      if (reservationId) {
        clearTimeout(reservationTimeoutId);
        try {
          await api.delete("/reservations/" + reservationId);
        } catch (err) {
          const status = getErrorStatus(err);
          if (status !== 404) {
            throw err;
          }
          // If already deleted server-side, continue with local cleanup.
        }
        // Prevent racing with a newly created reservation (remove -> add quickly).
        if (this.reservationId === reservationId) {
          this.reservationId = null;
          this.reservationTimeout = null;
        }
      }
      this.saveState();
    },

    async dropReservation() {
      if (this.reservationId) {
        this.getReservation().then((res) => {
          const reservationValidFrom = Date.parse(res.headers["x-serverutc"]);
          const reservationValidUntil = Date.parse(res.data.valid_until);
          const reservationTimeLeft = reservationValidUntil - reservationValidFrom;
          this.reservationTimeout = Date.now() + reservationTimeLeft;
          clearTimeout(reservationTimeoutId);
          reservationTimeoutId = setTimeout(() => this.dropReservation(), reservationTimeLeft);
          if (reservationTimeLeft < 5000) {
            clearTimeout(reservationTimeoutId);
            api.delete("/reservations/" + this.reservationId);
            this.reservationId = null;
            this.reservationTimeout = null;
            this.saveState();
          }
        });
      }
    },

    emptyCart() {
      clearTimeout(reservationTimeoutId);
      this._items = [];
      this.items = [];
      this.allocationId = null;
      this.reservationId = null;
      this.reservationTimeout = null;
      this.generalTermsAccepted = false;
      this.bookingEmail = undefined;
      this.contactDetails = {};
      this.paymentReference = null;
      this.bookingDetails = {};
      this.saveState();
    },

    showUpdate(item) {
      this.updates = [...this.updates.filter((x) => x !== item), item];
      setTimeout(() => {
        this.updates = this.updates.slice(1);
      }, 7500);
      this.updated = Date.now();
    },

    async getPaymentMethods() {
      if (this.reservationId) {
        const req = api.get("/reservations/" + this.reservationId + "/payment-methods");
        const res = await req;
        return res.data.payment_methods;
      } else {
        console.warn("cartStore.getPaymentMethods:", "no reservationId");
        return [];
      }
    },

    getGuestdataBasedOnCountry(ticketId, country) {
      return api.get(`/tickets/${ticketId}/guestdata?country_hint=${country.code3}`).then((res) => {
        return res.data.guest_data_requirements.filter(
          (req) => !["all_persons", "lead_guest", "pre_download", "pre_checkout"].includes(req)
        );
      });
    },
    async resetTimer() {
      if (!this.timeLeft) return;
      try {
        const response = await reservationsApi.reservationdetailresettimerPost({ reservationId: this.reservationId });

        //Force this to be UTC
        const timeLeft = new Date(response.validUntil + "Z") - Date.now();

        this.reservationTimeout = Date.now() + timeLeft;
        this.reservationTotalTime = timeLeft;

        this.saveState();
      } catch {
        //Do nothing, leave reservation as is
      }
    },
  },
});

export const initializeCartStore = () => {
  const cartStore = useCartStore();
  const userStore = useUserStore();

  watch(
    () => userStore.client_id,
    (value) => {
      if (!value) return;

      cartStore.loadState(value);
    },
    { immediate: true }
  );
};
